/**
 * PATH: packages/quote-builder/src/components/ComboField.tsx
 * PURPOSE: A labelled text input with an async results dropdown — free typing is
 *          always allowed (commits on blur), but matching records from a search
 *          callback can be picked to auto-fill related fields. Used by the
 *          Bravada Reference tab to autocomplete Client + Site from the report
 *          CRM. Debounced query, keyboard navigation, click-to-select.
 */

import { useEffect, useId, useRef, useState, type CSSProperties } from 'react'
import { createPortal } from 'react-dom'
import { Loader2, Search } from 'lucide-react'
import { cn } from '@quotech/ui'

// Pattern reused from: packages/job-hub/src/jobs/tabs/job-qa/QaPhotoSourceMenu.tsx (portal + flip)
// and packages/email-hub/src/feature/components/category-filter-bar/useAnchoredMenu.ts (useAnchoredMenu)
function anchoredMenuStyle(box: HTMLElement): CSSProperties {
  const rect = box.getBoundingClientRect()
  const spaceBelow = window.innerHeight - rect.bottom
  const spaceAbove = rect.top
  const flip = spaceBelow < 240 && spaceAbove > spaceBelow
  return {
    position: 'fixed',
    left: rect.left,
    width: rect.width,
    zIndex: 1000,
    ...(flip
      ? { bottom: window.innerHeight - rect.top + 4 }
      : { top: rect.bottom + 4 }),
  }
}

interface Props<T> {
  label: string
  value: string | null | undefined
  /** Commit free-typed text (on blur) when it changed. */
  onCommitText: (value: string) => void
  /** Async search; called debounced once the query is long enough. */
  search: (q: string) => Promise<T[]>
  getKey: (item: T) => string | number
  renderItem: (item: T) => React.ReactNode
  onSelect: (item: T) => void
  /** Text shown in the field immediately after a record is picked. */
  getDisplay?: (item: T) => string
  placeholder?: string
  readOnly?: boolean
  className?: string
  /** Override the input's styling (e.g. the cover page's print-style underline fields). */
  inputClassName?: string
  minChars?: number
  /**
   * Optional "create it" row under the results, for when the record does not
   * exist yet. Resolving to null leaves the typed text alone. Omitting this
   * keeps the field search-only, exactly as before.
   */
  onCreate?: (query: string) => Promise<T | null>
  createLabel?: (query: string) => React.ReactNode
  /** Blur restores the saved value instead of committing free text. */
  revertUnmatchedOnBlur?: boolean
  /** Draw the results list on document.body so a scrolling dialog cannot clip it. */
  portal?: boolean
  /** Shown when a search returns nobody. Keeps the list open. */
  emptyLabel?: string
}

export default function ComboField<T>({
  label,
  value,
  onCommitText,
  search,
  getKey,
  renderItem,
  onSelect,
  getDisplay,
  placeholder,
  readOnly,
  className,
  inputClassName,
  minChars = 2,
  onCreate,
  createLabel,
  revertUnmatchedOnBlur = false,
  portal = false,
  emptyLabel,
}: Props<T>) {
  const initial = value == null ? '' : String(value)
  const [buf, setBuf] = useState(initial)

  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [results, setResults] = useState<T[]>([])
  const [active, setActive] = useState(0)

  const boxRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const [menuStyle, setMenuStyle] = useState<CSSProperties | null>(null)
  const [failed, setFailed] = useState(false)
  const listId = useId()
  // Only the user typing should trigger a search — not programmatic value
  // changes (mount, or the full-quote reload after every save).
  const typingRef = useRef(false)
  const focusedRef = useRef(false)
  const pickedRef = useRef(false)

  // Keyed on `initial`, so this only fires on an EXTERNAL change — adopt it
  // even when the field has focus, or a focused-but-untouched combo goes on
  // showing a stale name after a save reload. Only live typing wins.
  useEffect(() => {
    if (!typingRef.current) setBuf(initial)
  }, [initial])

  const commit = () => {
    focusedRef.current = false
    if (revertUnmatchedOnBlur) {
      if (pickedRef.current) {
        pickedRef.current = false
        typingRef.current = false
        return
      }
      typingRef.current = false
      setBuf(initial)
      return
    }
    if (buf !== initial) onCommitText(buf)
  }

  // Debounced search — gated on the user actually typing.
  useEffect(() => {
    if (readOnly || !typingRef.current) return
    const q = buf.trim()
    if (q.length < minChars) {
      setResults([])
      setFailed(false)
      setOpen(false)
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    setOpen(true)
    const t = setTimeout(async () => {
      try {
        const hits = await search(q)
        if (cancelled) return
        setResults(hits)
        setFailed(false)
        setActive(0)
        // "No results" still opens the list when the field can create the
        // record — that empty state is where the create row lives.
        setOpen(hits.length > 0 || !!onCreate || (portal && !!emptyLabel))
      } catch {
        if (!cancelled) {
          setResults([])
          if (portal) {
            setFailed(true)
            setOpen(true)
          } else {
            setFailed(false)
            setOpen(false)
          }
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }, 180)
    return () => {
      cancelled = true
      clearTimeout(t)
    }
  }, [buf, minChars, readOnly, search, onCreate, portal, emptyLabel])

  useEffect(() => {
    if (!open || !portal) return
    const place = () => {
      if (boxRef.current) setMenuStyle(anchoredMenuStyle(boxRef.current))
    }
    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [open, portal, buf])

  // Close on outside click.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (boxRef.current?.contains(target)) return
      if (listRef.current?.contains(target)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  const choose = (item: T) => {
    // Reflect the picked record instantly; suppress the buffer-driven search.
    typingRef.current = false
    pickedRef.current = true
    if (getDisplay) setBuf(getDisplay(item))
    onSelect(item)
    setOpen(false)
    setResults([])
    setLoading(false)
  }

  const [creating, setCreating] = useState(false)
  const runCreate = async () => {
    const q = buf.trim()
    if (!onCreate || creating || q.length < minChars) return
    setCreating(true)
    try {
      const made = await onCreate(q)
      if (made) choose(made)
      else setOpen(false)
    } finally {
      setCreating(false)
    }
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!open || results.length === 0) {
      if (e.key === 'Escape') setOpen(false)
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive((a) => (a + 1) % results.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((a) => (a - 1 + results.length) % results.length)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      // A partial list must not bind the first hit. Enter selects only when
      // one result's display name is the text the user typed.
      const typed = buf.trim().toLowerCase()
      const exact = results.filter((item) => {
        const label = (getDisplay ? getDisplay(item) : '').trim().toLowerCase()
        return typed.length > 0 && label === typed
      })
      if (exact.length === 1) choose(exact[0])
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  return (
    <label className={cn('block', className)}>
      <span className="mb-1 block text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">{label}</span>
      <div ref={boxRef} className="relative">
        <input
          type="text"
          value={buf}
          readOnly={readOnly}
          placeholder={placeholder}
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          autoComplete="off"
          onChange={(e) => {
            typingRef.current = true
            pickedRef.current = false
            setBuf(e.target.value)
          }}
          onFocus={() => {
            focusedRef.current = true
            if (results.length > 0) setOpen(true)
          }}
          onBlur={commit}
          onKeyDown={onKeyDown}
          className={
            inputClassName ??
            'w-full rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 pr-8 text-sm text-foreground shadow-sm outline-none transition-colors placeholder:text-muted-foreground/60 focus:border-[#5B2D87] focus:bg-white focus:ring-2 focus:ring-[#5B2D87]/20 dark:border-slate-600 dark:bg-slate-800/60 dark:shadow-none dark:focus:border-[#9d6fd0] dark:focus:bg-slate-900 dark:focus:ring-[#9d6fd0]/25'
          }
        />
        <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
        </span>
        {open && (loading || results.length > 0 || !!onCreate || failed || (!!emptyLabel && results.length === 0)) && (() => {
          const list = (
          <ul
            ref={listRef}
            id={listId}
            role="listbox"
            style={portal ? menuStyle ?? undefined : undefined}
            className={portal
              ? 'max-h-60 overflow-auto rounded-md border border-slate-200 bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-900'
              : 'absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-slate-200 bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-900'}
          >
            {loading && results.length === 0 && (
              <li className="px-2.5 py-1.5 text-xs text-slate-400">Searching…</li>
            )}
            {failed && (
              <li className="px-2.5 py-1.5 text-xs text-slate-500">Search failed. Try again.</li>
            )}
            {!loading && !failed && results.length === 0 && emptyLabel && (
              <li className="px-2.5 py-1.5 text-xs text-slate-500">{emptyLabel}</li>
            )}
            {results.map((item, i) => (
              <li
                key={getKey(item)}
                role="option"
                aria-selected={i === active}
                onMouseDown={(e) => {
                  e.preventDefault()
                  choose(item)
                }}
                onMouseEnter={() => setActive(i)}
                className={cn(
                  'cursor-pointer px-2.5 py-1.5 text-xs',
                  i === active ? 'bg-primary/10 text-foreground' : 'text-slate-600 dark:text-slate-300',
                )}
              >
                {renderItem(item)}
              </li>
            ))}
            {onCreate && !loading && buf.trim().length >= minChars && (
              <li
                role="option"
                aria-selected={false}
                onMouseDown={(e) => {
                  e.preventDefault()
                  void runCreate()
                }}
                className="cursor-pointer border-t border-slate-100 px-2.5 py-1.5 text-xs font-medium text-primary hover:bg-primary/10 dark:border-slate-800"
              >
                {creating ? 'Creating…' : (createLabel?.(buf.trim()) ?? `Create “${buf.trim()}”`)}
              </li>
            )}
          </ul>
          )
          if (portal && menuStyle) return createPortal(list, document.body)
          if (portal) return null
          return list
        })()}
      </div>
    </label>
  )
}
